import { useRouter } from "expo-router";
import { useMemo, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";

import {
  fontFamily,
  radius,
  space,
  textStyle,
} from "@passfirst/ui";

import { authClient } from "../src/auth-client";
import { Button } from "../src/components/button";
import { Screen } from "../src/components/screen";
import { ShieldIcon } from "../src/icons";
import { Lockup } from "../src/lockup";
import { clearAnonAttempts, encodeAnonClaim } from "../src/practice/anon-attempts";
import { getStoredSelection } from "../src/selected-vertical-store";
import { type Colors, useColors } from "../src/theme";

// Step 4 of onboarding — the "save your progress" email gate. Fires
// after the first 5 free questions deliver the Socratic-tutor aha. By
// the time the user gets here they've already had value delivered, so
// the ask is "save what you just did" not "register to use the
// product." Skip-for-now is one tap, never buried.

export default function EmailGate(): JSX.Element {
  const router = useRouter();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(): Promise<void> {
    if (!email.includes("@") || !email.includes(".")) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Thread the anonymous attempts into the magic-link callback so
      // index.tsx persists them via questions.claimAnonymous once the
      // user lands back signed-in. Without a claim, fall back to /practice.
      const claim = encodeAnonClaim();
      const callbackURL = claim
        ? `/?claim=${encodeURIComponent(claim)}`
        : "/practice";
      const res = await authClient.signIn.magicLink({ email, callbackURL });
      if (res.error) {
        setError(res.error.message ?? "Couldn't send magic link.");
        return;
      }
      // The attempts now live in the emailed link; drop the local copy so
      // a resend (or a later anonymous batch) can't double-claim them.
      clearAnonAttempts();
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "send failed");
    } finally {
      setBusy(false);
    }
  }

  // Forward the stored credential so /practice doesn't error with "No
  // vertical selected" when the user skips or keeps practicing.
  function goToPractice(): void {
    const stored = getStoredSelection();
    if (stored) {
      router.replace({
        pathname: "/practice",
        params: {
          vertical: stored.verticalSlug,
          ...(stored.jurisdictionCode
            ? { jurisdiction: stored.jurisdictionCode }
            : {}),
        },
      });
    } else {
      router.replace("/practice");
    }
  }

  if (sent) {
    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        style={{ flex: 1 }}
      >
      <Screen>
        <View style={styles.topBar}>
          <Lockup size={26} />
        </View>
        <View style={styles.scrollContent}>
          <Text style={styles.eyebrow}>CHECK YOUR INBOX</Text>
          <Text style={styles.title}>
            We sent a link to{"\n"}
            <Text style={styles.emailEmphasis}>{email}</Text>
          </Text>
          <Text style={styles.lede}>
            Click the link to save your progress. You can keep practicing here
            in the meantime.
          </Text>
          <Button
            label="Keep practicing"
            onPress={goToPractice}
            fullWidth
            style={styles.ctaSpacing}
          />
        </View>
      </Screen>
      </KeyboardAvoidingView>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1 }}
    >
    <Screen>
      <View style={styles.topBar}>
        <Lockup size={26} />
      </View>
      <View style={styles.scrollContent}>
        <Text style={styles.eyebrow}>NICE WORK SO FAR</Text>
        <Text style={styles.title}>
          Save your progress{"\n"}& keep going.
        </Text>
        <Text style={styles.lede}>
          You&apos;ve got <Text style={styles.bold}>more free questions</Text> and
          your readiness diagnostic waiting. Drop your email and we&apos;ll save
          your place — magic link, no password.
        </Text>

        <TextInput
          value={email}
          onChangeText={(t) => {
            setEmail(t);
            if (error) setError(null);
          }}
          placeholder="you@email.com"
          placeholderTextColor={colors.textMuted}
          autoCapitalize="none"
          keyboardType="email-address"
          autoComplete="email"
          inputMode="email"
          accessibilityLabel="Email address"
          style={styles.input}
        />

        {error ? (
          <Text style={styles.error} accessibilityLiveRegion="polite">
            {error}
          </Text>
        ) : null}

        <Button
          label="Continue & keep practicing free"
          onPress={handleSubmit}
          disabled={email.length === 0}
          busy={busy}
          fullWidth
          accessibilityLabel="Continue and keep practicing free"
          style={styles.ctaSpacing}
        />

        <View style={styles.trustRow}>
          <ShieldIcon size={16} color={colors.textMuted} />
          <Text style={styles.trustText}>
            We never sell your data. Email and payment only — nothing more.
          </Text>
        </View>

        <View style={styles.spacer} />

        <Button
          label="Skip for now"
          onPress={goToPractice}
          variant="ghost"
          fullWidth
          accessibilityLabel="Skip for now"
        />
      </View>
    </Screen>
    </KeyboardAvoidingView>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  topBar: {
    alignItems: "center",
    paddingTop: space[8],
    paddingBottom: space[2],
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSoft,
  },
  scrollContent: {
    flex: 1,
    padding: space[6],
    paddingTop: space[8],
    paddingBottom: space[10],
    maxWidth: 440,
    alignSelf: "center",
    width: "100%",
  },
  eyebrow: {
    ...textStyle.eyebrow,
    color: colors.action,
    marginBottom: space[3],
  },
  title: {
    fontFamily: fontFamily.extraBold,
    fontSize: 32,
    lineHeight: 36,
    letterSpacing: -0.5,
    color: colors.heading,
    marginBottom: space[3],
  },
  lede: {
    ...textStyle.body,
    color: colors.textSecondary,
    marginBottom: space[6],
  },
  bold: { fontFamily: fontFamily.bold, color: colors.textPrimary },
  emailEmphasis: { color: colors.action },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.lg,
    padding: 15,
    fontFamily: fontFamily.regular,
    fontSize: 16,
    color: colors.textPrimary,
    backgroundColor: colors.surface,
    marginBottom: space[3],
  },
  ctaSpacing: {
    marginBottom: space[4],
  },
  trustRow: {
    flexDirection: "row",
    gap: space[2],
    alignItems: "center",
    marginTop: space[2],
  },
  trustText: {
    ...textStyle.caption,
    color: colors.textMuted,
    flex: 1,
    lineHeight: 18,
  },
  spacer: { flex: 1, minHeight: space[6] },
  error: {
    ...textStyle.caption,
    color: colors.error,
    marginBottom: space[3],
  },
  });
}
